import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWizardStore, type WizardType, type DivisionKind, type InstallTier } from '../store/wizardStore';
import { StepperNav } from '../components/StepperNav';
import { SaveProjectButton } from '../components/SaveProjectButton';
import { usePricing, formatPrice } from '../services/pricing';

// P0-5: service-level choices surfaced early (compact). Detail lives in the cart.
const TIER_CHOICES: { value: InstallTier; label: string }[] = [
  { value: 'material', label: 'Só material' },
  { value: 'electrica', label: '+ Instalação' },
  { value: 'config', label: '+ Configuração' },
  { value: 'full', label: 'Chave-na-mão' },
];

const KIND_EMOJI: Record<string, string> = {
  sala: '🛋️', cozinha: '🍳', quarto: '🛏️', wc: '🚿',
  corredor: '🚪', escritorio: '💻', garagem: '🚗', jardim: '🌿', outro: '⬜',
};

// Units in a division = combo units (qty) + standalone products. Combo SKUs
// are mirrored into `selections`, so standalone = selections minus combo SKUs.
function divisionItemCount(
  combos: { skus: string[]; qty: number }[] | undefined,
  skus: string[] | undefined,
): { hasItems: boolean; displayCount: number } {
  const comboQty = combos?.reduce((s, c) => s + (c.qty ?? 1), 0) ?? 0;
  const comboSkus = new Set(combos?.flatMap((c) => c.skus) ?? []);
  const standalone = (skus ?? []).filter((s) => !comboSkus.has(s)).length;
  const displayCount = comboQty + standalone;
  return { hasItems: displayCount > 0, displayCount };
}

const KIND_OPTIONS: { value: DivisionKind; label: string }[] = [
  { value: 'sala', label: 'Sala' },
  { value: 'cozinha', label: 'Cozinha' },
  { value: 'quarto', label: 'Quarto' },
  { value: 'wc', label: 'WC / Casa de banho' },
  { value: 'corredor', label: 'Corredor / Hall' },
  { value: 'escritorio', label: 'Escritório' },
  { value: 'garagem', label: 'Garagem' },
  { value: 'jardim', label: 'Jardim / Exterior' },
  { value: 'outro', label: 'Outro' },
];

export function DivisionGridPage() {
  const { type } = useParams<{ type: WizardType }>();
  const navigate = useNavigate();
  const { divisions, toggleDivision, setActiveDivision, selections, devices, renameDivision, addDivision, installTier, setInstallTier } =
    useWizardStore();

  // P0-4: global running estimate (material, VAT-incl) across all divisions.
  const allSkuQty = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of divisions) {
      // Combos + standalone products coexist: combo SKUs are mirrored into
      // `selections`, so standalone = selections minus combo SKUs. Counting
      // only one of the two dropped the other from the estimate.
      const comboSkus = new Set<string>();
      for (const c of devices[d.id] ?? []) {
        const q = c.qty ?? 1;
        for (const s of c.skus) { m.set(s, (m.get(s) ?? 0) + q); comboSkus.add(s); }
      }
      for (const s of selections[d.id] ?? []) {
        if (!comboSkus.has(s)) m.set(s, (m.get(s) ?? 0) + 1);
      }
    }
    return m;
  }, [divisions, devices, selections]);
  const allPricing = usePricing([...allSkuQty.keys()]);
  const { runningTotal, allPriced } = useMemo(() => {
    let runningTotal = 0;
    let allPriced = allSkuQty.size > 0;
    for (const [sku, qty] of allSkuQty) {
      const p = allPricing[sku]?.priceEur ?? null;
      if (p == null || p <= 0) allPriced = false;
      else runningTotal += p * qty;
    }
    return { runningTotal, allPriced };
  }, [allSkuQty, allPricing]);

  // Split into per-room divisions and the global 'casa' virtual division
  const roomDivisions = divisions.filter((d) => d.kind !== 'casa');
  const casaDivisions = divisions.filter((d) => d.kind === 'casa');
  const activeDivisions = divisions.filter((d) => d.optedIn);
  const divisionsWithItems = divisions.filter(
    (d) => (devices[d.id]?.length ?? 0) > 0 || (selections[d.id] ?? []).length > 0,
  );

  // Inline rename state — null means not renaming
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Add division form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKind, setNewKind] = useState<DivisionKind>('outro');
  const [newLabel, setNewLabel] = useState('');

  function handleEdit(divId: string) {
    setActiveDivision(divId);
    navigate(`/wizard/${type}/edit`);
  }

  function handleFinish() {
    navigate(`/wizard/${type}/cor`);
  }

  function startRename(divId: string, currentLabel: string, e: React.MouseEvent) {
    e.stopPropagation();
    setRenamingId(divId);
    setRenameValue(currentLabel);
  }

  function commitRename() {
    if (renamingId && renameValue.trim()) {
      renameDivision(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  }

  function handleAddDivision() {
    const kindLabel = KIND_OPTIONS.find((k) => k.value === newKind)?.label ?? newKind;
    const label = newLabel.trim() || kindLabel;
    const id = `custom-${Date.now()}`;
    addDivision({ id, kind: newKind, label, optedIn: true, categoryId: newKind });
    setNewLabel('');
    setNewKind('outro');
    setShowAddForm(false);
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <StepperNav current={1} />
      <div className="mb-6 mt-4">
        <button
          type="button"
          onClick={() => navigate('/wizard')}
          className="mb-4 text-sm text-smartify-muted hover:text-smartify-ink"
        >
          ← Voltar
        </button>
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-bold text-smartify-ink">
            Divisões — {type}
          </h1>
          <SaveProjectButton />
        </div>
        {/* Onboarding hint */}
        <p className="mt-1 text-sm text-smartify-muted">
          Toca em cada divisão para adicionar equipamentos.
        </p>
      </div>

      {divisions.length === 0 && (
        <div className="rounded-smartify border border-smartify-line bg-white p-6 text-center text-sm text-smartify-muted">
          A carregar divisões…
        </div>
      )}

      <ul className="space-y-2">
        {roomDivisions.map((div) => {
          const { hasItems, displayCount } = divisionItemCount(devices[div.id], selections[div.id]);
          const emoji = KIND_EMOJI[div.kind] ?? '⬜';
          const isRenaming = renamingId === div.id;
          return (
            <li key={div.id}>
              <div
                className={[
                  'flex items-center gap-3 rounded-smartify border p-3 transition-colors',
                  div.optedIn
                    ? 'border-smartify-brand/30 bg-white hover:border-smartify-brand/60'
                    : 'border-smartify-line bg-white opacity-60 hover:opacity-80',
                ].join(' ')}
              >
                {/* Main CTA — card left side clickable area */}
                <button
                  type="button"
                  onClick={() => {
                    if (!div.optedIn) toggleDivision(div.id);
                    handleEdit(div.id);
                  }}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  aria-label={`Configurar ${div.label}`}
                >
                  <span className="shrink-0 text-lg">{emoji}</span>
                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <input
                        autoFocus
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={commitRename}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        className="w-full rounded border border-smartify-brand px-2 py-0.5 text-sm font-medium text-smartify-ink focus:outline-none"
                      />
                    ) : (
                      <p className="font-medium text-smartify-ink">{div.label}</p>
                    )}
                    {/* Status badge */}
                    {!isRenaming && (
                      hasItems ? (
                        <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-green-700">
                          <span aria-hidden>✓</span>
                          {displayCount} {displayCount === 1 ? 'componente' : 'componentes'}
                        </span>
                      ) : (
                        <span className="mt-0.5 inline-block text-xs text-smartify-muted">
                          por configurar
                        </span>
                      )
                    )}
                  </div>
                  {/* Arrow CTA */}
                  {!isRenaming && (
                    <span className="shrink-0 rounded-smartify bg-smartify-brand px-3 py-1.5 text-xs font-semibold text-white">
                      {hasItems ? 'Editar →' : 'Configurar →'}
                    </span>
                  )}
                </button>

                {/* Secondary controls: rename + opt-in toggle */}
                {!isRenaming && (
                  <div className="flex shrink-0 items-center gap-1 border-l border-smartify-line pl-2">
                    <button
                      type="button"
                      onClick={(e) => startRename(div.id, div.label, e)}
                      aria-label={`Renomear ${div.label}`}
                      className="rounded p-1 text-smartify-muted hover:text-smartify-ink"
                    >
                      ✏️
                    </button>
                    {/* Small toggle */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={div.optedIn}
                      aria-label={div.optedIn ? `Excluir ${div.label}` : `Incluir ${div.label}`}
                      onClick={() => toggleDivision(div.id)}
                      className={[
                        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none',
                        div.optedIn ? 'bg-smartify-brand' : 'bg-gray-200',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                          div.optedIn ? 'translate-x-4' : 'translate-x-0',
                        ].join(' ')}
                      />
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Global home products — "Para toda a casa" */}
      {casaDivisions.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
            Para toda a casa
          </p>
          <ul className="space-y-2">
            {casaDivisions.map((div) => {
              const { hasItems, displayCount } = divisionItemCount(devices[div.id], selections[div.id]);
              const isRenaming = renamingId === div.id;
              return (
                <li key={div.id}>
                  <div
                    className={[
                      'flex items-center gap-3 rounded-smartify border p-3 transition-colors',
                      div.optedIn
                        ? 'border-amber-300 bg-amber-50 hover:border-amber-400'
                        : 'border-amber-200 bg-amber-50/50 opacity-60',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!div.optedIn) toggleDivision(div.id);
                        setActiveDivision(div.id);
                        navigate(`/wizard/${type}/edit`);
                      }}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      aria-label={`Configurar ${div.label}`}
                    >
                      <span className="shrink-0 text-lg">🏠</span>
                      <div className="min-w-0 flex-1">
                        {isRenaming ? (
                          <input
                            autoFocus
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={commitRename}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitRename();
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            className="w-full rounded border border-amber-400 px-2 py-0.5 text-sm font-medium focus:outline-none"
                          />
                        ) : (
                          <p className="font-medium text-smartify-ink">{div.label}</p>
                        )}
                        {!isRenaming && (
                          hasItems ? (
                            <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-green-700">
                              <span aria-hidden>✓</span>
                              {displayCount} {displayCount === 1 ? 'componente' : 'componentes'}
                            </span>
                          ) : (
                            <p className="mt-0.5 text-xs text-amber-700">
                              Fechadura · Alarme · Câmaras · Videoporteiro
                            </p>
                          )
                        )}
                      </div>
                      {!isRenaming && (
                        <span className="shrink-0 rounded-smartify bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white">
                          {hasItems ? 'Editar →' : 'Configurar →'}
                        </span>
                      )}
                    </button>

                    {/* Secondary: toggle */}
                    {!isRenaming && (
                      <div className="flex shrink-0 items-center gap-1 border-l border-amber-200 pl-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={div.optedIn}
                          aria-label={div.optedIn ? `Excluir ${div.label}` : `Incluir ${div.label}`}
                          onClick={() => toggleDivision(div.id)}
                          className={[
                            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none',
                            div.optedIn ? 'bg-amber-500' : 'bg-gray-200',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                              div.optedIn ? 'translate-x-4' : 'translate-x-0',
                            ].join(' ')}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Add division */}
      {showAddForm ? (
        <div className="mt-3 rounded-smartify border border-smartify-brand/40 bg-blue-50 p-4 space-y-3">
          <p className="text-sm font-semibold text-smartify-ink">Nova divisão</p>
          <div className="flex gap-2">
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as DivisionKind)}
              className="flex-1 rounded-smartify border border-smartify-line bg-white px-3 py-2 text-sm focus:border-smartify-brand"
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k.value} value={k.value}>
                  {KIND_EMOJI[k.value]} {k.label}
                </option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Nome da divisão (opcional)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddDivision(); if (e.key === 'Escape') setShowAddForm(false); }}
            className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm focus:border-smartify-brand"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddDivision}
              className="flex-1 rounded-smartify bg-smartify-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Adicionar
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="rounded-smartify border border-smartify-line px-4 py-2 text-sm text-smartify-muted hover:text-smartify-ink"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="mt-3 w-full rounded-smartify border-2 border-dashed border-smartify-line px-4 py-2.5 text-sm font-medium text-smartify-muted hover:border-smartify-brand hover:text-smartify-brand"
        >
          + Adicionar divisão
        </button>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => navigate(`/wizard/${type}/planta`)}
          className="w-full rounded-smartify border border-smartify-line bg-white px-4 py-2.5 text-sm font-medium text-smartify-ink hover:border-smartify-accent"
        >
          🗺️ Planta da casa — posicionar divisões e descarregar mapa (opcional)
        </button>
      </div>

      {/* P0-5: service level, chosen early */}
      <div className="mt-6 rounded-smartify border border-smartify-line bg-white p-4">
        <p className="text-sm font-semibold text-smartify-ink">Como queres avançar?</p>
        <p className="mb-2 text-xs text-smartify-muted">Material só, ou com instalação/configuração da nossa equipa. Podes mudar depois.</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {TIER_CHOICES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setInstallTier(t.value)}
              aria-pressed={installTier === t.value}
              className={[
                'rounded-smartify border px-3 py-2 text-xs font-medium transition-colors',
                installTier === t.value
                  ? 'border-smartify-brand bg-smartify-brand/5 text-smartify-ink'
                  : 'border-smartify-line text-smartify-ink hover:border-smartify-brand/40',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* P0-4: running material estimate */}
      {allSkuQty.size > 0 && (
        <div className="mt-3 flex items-center justify-between rounded-smartify border border-smartify-line bg-smartify-paper px-4 py-3">
          <span className="text-sm text-smartify-muted">
            Estimativa de material{allPriced ? '' : ' (parcial)'}
            {installTier !== 'material' && <span className="block text-xs">+ instalação calculada no orçamento</span>}
          </span>
          <span className="text-lg font-bold text-smartify-brand">
            {runningTotal > 0 ? `${allPriced ? '' : 'desde '}${formatPrice(runningTotal)}` : 'sob consulta'}
          </span>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <p className="text-sm text-smartify-muted">
          {divisionsWithItems.length} {divisionsWithItems.length === 1 ? 'divisão' : 'divisões'} com equipamentos
          {' / '}
          {activeDivisions.length} {activeDivisions.length === 1 ? 'activa' : 'activas'}
        </p>
        <button
          type="button"
          disabled={activeDivisions.length === 0}
          onClick={handleFinish}
          className="rounded-smartify bg-smartify-brand px-8 py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          Continuar →
        </button>
      </div>
    </div>
  );
}
